import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import useAuth from 'hooks/useAuth'
import { useUserLoginHistory } from 'hooks/useLoginHistory'
import { useRoles } from 'hooks/useRoles'
import {
  useCreateUser,
  useDeleteUserFromOrganization,
  useFetchUser,
  useRequireUserResetPassword,
  useSendUserInvite,
  useUpdateUser,
  useSendUserChangePassword,
} from 'hooks/useUsers'
import React, { createContext, useContext, useEffect, useState } from 'react'
import type { LoginHistoryItem } from 'types/login-history'
import type { OrganizationRolePair, Role } from 'types/role'
import { lookupGuestRole, lookupMemberRole } from 'types/role'
import { UserProfile, getTopParentOrganizationId } from 'types/user-profile'
import { UserFormProps } from '../types/types'

dayjs.extend(utc)

interface EditUserDialogContextType {
  // State
  activeTab: number
  formDataChanged: boolean
  formData: UserFormProps
  rootOrganizationId: string | null

  // User data
  user: UserProfile | undefined
  isLoadingUser: boolean
  refetchUserProfile: () => void

  // Roles data
  roles: Role[]
  isLoadingRoles: boolean
  isRolesError: boolean

  // Auth data
  profile: UserProfile | undefined
  token: string | null
  isLoadingProfile: boolean

  // Login history data
  loginHistory: LoginHistoryItem[]
  isLoadingLoginHistory: boolean
  isLoginHistoryError: boolean

  // Loading states
  isCreating: boolean
  isUpdating: boolean
  updateUserError: Error | null
  isDeletingUser: boolean
  isRequiringPasswordReset: boolean
  isSendingInvite: boolean
  isSendingUserChangePassword: boolean

  // Password reset functionality
  requireUserResetPassword: (userId: string) => Promise<{ success: boolean; data: any }>

  sendUserInvite: (userId: string) => Promise<{ success: boolean; data: any }>
  sendUserChangePassword: (userId: string) => Promise<{ success: boolean; data: any }>

  // Methods
  setActiveTab: (tab: number) => void
  handleFormDataChange: (newData: UserFormProps) => void
  handleSave: () => Promise<void>
  handleDeleteUser: (userId: string | null, rootOrganizationId: string) => void
  validateForm: () => boolean
  validateRoles: () => boolean
  getFormValidationErrorMessages: () => string
  resetFormData: () => void
}

const EditUserDialogContext = createContext<EditUserDialogContextType | undefined>(undefined)

interface EditUserDialogProviderProps {
  children: React.ReactNode
  open: boolean
  createMode: boolean
  onClose: () => void
  userId: string | null
}

export const EditUserDialogProvider: React.FC<EditUserDialogProviderProps> = ({
  children,
  open,
  createMode,
  onClose,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const { profile, token, isLoading: isLoadingProfile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string | null>(null)

  const [formDataChanged, setFormDataChanged] = useState(false)
  const [formData, setFormData] = useState<UserFormProps>({
    id: null,
    firstName: '',
    lastName: '',
    email: '',
    position: '',
    startDate: null,
    endDate: null,
    ledgerAccountId: '',
    externalId: '',
    assignedRoles: [],
  })

  const {
    userProfile: user,
    isLoading: isLoadingUser,
    mutate: refetchUserProfile,
  } = useFetchUser(token!, userId!, [['explicitRolesOnly', 'true']], !!userId)
  const { roles, isLoading: isLoadingRoles, isError: isRolesError } = useRoles(token!, true)
  const {
    loginHistory,
    isLoading: isLoadingLoginHistory,
    isError: isLoginHistoryError,
  } = useUserLoginHistory(token!, userId!)

  const { createUser, isLoading: isCreating } = useCreateUser(token!)
  const { updateUser, isLoading: isUpdating, error: updateUserError } = useUpdateUser(token!)
  const { deleteUser, isLoading: isDeletingUser } = useDeleteUserFromOrganization(token!)
  const { requireUserResetPassword, isSending: isRequiringPasswordReset } = useRequireUserResetPassword(token!)
  const { sendUserInvite, isSending: isSendingInvite } = useSendUserInvite(token!)
  const { sendUserChangePassword, isSending: isSendingUserChangePassword } = useSendUserChangePassword(token!)

  const resetFormData = () => {
    setFormData({
      id: null,
      firstName: '',
      lastName: '',
      email: '',
      position: '',
      startDate: null,
      endDate: null,
      ledgerAccountId: '',
      externalId: '',
      assignedRoles: [],
    })
  }

  useEffect(() => {
    if (profile) {
      const firstRootOrgId = getTopParentOrganizationId(profile)
      setRootOrganizationId(firstRootOrgId)
    }
  }, [profile])

  // Fetch user data in edit mode and reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (!createMode && userId) {
        // Load user data when editing an existing user
        refetchUserProfile()
      } else if (createMode) {
        // Reset form when creating a new user
        resetFormData()
        setFormDataChanged(false)
      }
    } else {
      // Reset form when dialog closes
      resetFormData()
      setFormDataChanged(false)
      setActiveTab(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, createMode, open])

  useEffect(() => {
    if (!createMode && !isLoadingUser && user && open) {
      setFormData({
        id: user.user.id || null,
        firstName: user.user.firstName || '',
        lastName: user.user.lastName || '',
        email: user.user.email || '',
        position: user.user.position || '',
        externalId: user.user.externalId || '',
        ledgerAccountId: user.user.ledgerAccountId,
        startDate: user.user.startDate ? dayjs(user.user.startDate) : null,
        endDate: user.user.endDate ? dayjs(user.user.endDate) : null,
        assignedRoles: user.roles.map((role: any) => ({
          organizationId: role.organization.id,
          roleId: role.role.id,
          delete: false,
        })) || [{ organizationId: '', roleId: '', delete: false }],
      })
      setFormDataChanged(false)
    }
  }, [createMode, user, isLoadingUser, open])

  const handleSave = async () => {
    if (!validateForm()) {
      const formErrorMessage = getFormValidationErrorMessages()
      console.log(formErrorMessage)

      // TODO: Add show error message
      return
    }
    try {
      const updatedRoles: Array<OrganizationRolePair> = formData.assignedRoles.filter((role) => role.delete != true)
      if (createMode) {
        await createUser({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          position: formData.position,
          startDate: formData.startDate ? formData.startDate.utc().toISOString() : '',
          endDate: formData.endDate ? formData.endDate.utc().toISOString() : null,
          ledgerAccountId: formData.ledgerAccountId || undefined,
          externalId: formData.externalId || null,
          roles: updatedRoles,
        })
      } else if (formData.id) {
        await updateUser(formData.id, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          position: formData.position,
          ledgerAccountId: formData.ledgerAccountId || undefined,
          startDate: formData.startDate ? formData.startDate.utc().toISOString() : '',
          endDate: formData.endDate ? formData.endDate.utc().toISOString() : null,
          externalId: formData.externalId || null,
          roles: updatedRoles,
        })
      }
      resetFormData()
      onClose()
    } catch (error) {
      console.error('Error saving user:', error)
      // TODO: Add error handling/display
    }
  }

  const handleDeleteUser = (userId: string | null, rootOrganizationId: string) => {
    if (!userId) return
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      deleteUser(userId, rootOrganizationId)
        .then((success) => {
          if (success) {
            resetFormData()
            onClose()
            return
          } else {
            console.error('Failed to delete user')
          }
        })
        .catch((error) => {
          console.error('Error archiving user:', error)
        })
    }
  }

  const handleFormDataChange = (newData: UserFormProps) => {
    setFormDataChanged(true)
    setFormData({ ...newData })
  }

  const validateRoles = () => {
    if (isLoadingRoles || !roles) return false

    const guestRoleId = lookupGuestRole(roles)?.id || ''
    const memberRoleId = lookupMemberRole(roles)?.id || ''

    return (
      formData.assignedRoles.filter((role) => role.roleId === guestRoleId || role.roleId === memberRoleId).length > 0
    )
  }

  const validateForm = () => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      (createMode ? formData.startDate !== null : true) &&
      (createMode ? formData.ledgerAccountId !== '' : true) &&
      validateRoles()
    )
  }

  const getFormValidationErrorMessages = () => {
    const messages: string[] = []
    return messages.join('\n')
  }

  const contextValue: EditUserDialogContextType = {
    // State
    activeTab,
    formDataChanged,
    formData,
    rootOrganizationId,

    // User data
    user,
    isLoadingUser,
    refetchUserProfile,

    // Roles data
    roles: roles || [],
    isLoadingRoles,
    isRolesError,

    // Auth data
    profile,
    token,
    isLoadingProfile,

    // Login history data
    loginHistory: loginHistory || [],
    isLoadingLoginHistory,
    isLoginHistoryError,

    // Loading states
    isCreating,
    isUpdating,
    updateUserError,
    isDeletingUser,
    isRequiringPasswordReset,
    isSendingInvite,
    isSendingUserChangePassword,

    // Password reset functionality
    requireUserResetPassword,

    sendUserInvite,
    sendUserChangePassword,

    // Methods
    setActiveTab,
    handleFormDataChange,
    handleSave,
    handleDeleteUser,
    validateForm,
    validateRoles,
    getFormValidationErrorMessages,
    resetFormData,
  }

  return <EditUserDialogContext.Provider value={contextValue}>{children}</EditUserDialogContext.Provider>
}

export const useEditUserDialog = () => {
  const context = useContext(EditUserDialogContext)
  if (context === undefined) {
    throw new Error('useEditUserDialog must be used within a EditUserDialogProvider')
  }
  return context
}
